import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePublicDatasets, useRepositoryStats, useDownloadManager } from '../../services';
import { dataOverviewApi, repositoriesApi } from '../../services/endpoints';
import { saveNavigationState } from '../../utils/navigationStateManager';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Stepper } from '../ui/Stepper';
import { Database, MagnifyingGlass, Users, CalendarBlank, CheckCircle, WarningCircle, CircleNotch, DownloadSimple, XCircle, CaretDown } from '@phosphor-icons/react';
import type { MetadataInfo, DownloadJob } from '../../services/types';

// Debounce helper
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

export function PublicData() {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  // State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterOrganism, setFilterOrganism] = useState<string>('human');
  const [selectedDatasets, setSelectedDatasets] = useState<string[]>([]);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [isInitiatingDownloads, setIsInitiatingDownloads] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const [expandedAbstracts, setExpandedAbstracts] = useState<Set<string>>(new Set());
  const [metadataInfo, setMetadataInfo] = useState<MetadataInfo | null>(null);
  
  // Debounce search term
  const debouncedSearchTerm = useDebounce(searchTerm, 400);
  
  // Fetch repository stats
  const { stats, loading: statsLoading } = useRepositoryStats(true);
  
  // Fetch datasets
  const {
    datasets,
    loading: datasetsLoading,
    error: datasetsError,
    search,
    refetch,
    hasMore,
    loadMore,
  } = usePublicDatasets({
    organism: filterOrganism,
    limit: 15,
    auto: !isSearchMode,
  });
  
  // DownloadSimple manager
  const {
    startDownload,
    getJobStatus,
    isDownloading,
    isCompleted,
    isFailed,
  } = useDownloadManager({
    pollInterval: 2000,
    onComplete: (job) => {
      console.log('DownloadSimple completed:', job.project_id);
    },
    onError: (job) => {
      console.error('DownloadSimple failed:', job.project_id, job.error_message);
    },
  });
  
  // Track mapping between project_id and job_id
  const [projectJobMap, setProjectJobMap] = useState<Map<string, string>>(new Map());
  
  // Ref to prevent duplicate navigation
  const isNavigatingRef = useRef(false);
  
  // Available organisms from stats
  const organisms = useMemo(() => {
    if (stats?.organisms) {
      return Object.keys(stats.organisms);
    }
    return ['human', 'mouse'];
  }, [stats]);
  
  // Memoized download status checks for performance
  const downloadStatusMap = useMemo(() => {
    const map = new Map<string, {
      jobId?: string;
      downloading: boolean;
      completed: boolean;
      failed: boolean;
      job: DownloadJob | null;
    }>();
    
    projectJobMap.forEach((jobId, projectId) => {
      map.set(projectId, {
        jobId,
        downloading: isDownloading(jobId),
        completed: isCompleted(jobId),
        failed: isFailed(jobId),
        job: getJobStatus(jobId),
      });
    });
    
    return map;
  }, [projectJobMap, isDownloading, isCompleted, isFailed, getJobStatus]);
  
  // Handle search
  useEffect(() => {
    if (debouncedSearchTerm.trim()) {
      if (!isSearchMode) {
        setIsSearchMode(true);
      }
      search({
        organism: filterOrganism,
        project_id: debouncedSearchTerm.includes('SRP') || debouncedSearchTerm.includes('ERP') || debouncedSearchTerm.includes('DRP') 
          ? debouncedSearchTerm.trim() 
          : undefined,
        study: debouncedSearchTerm.trim(),
        limit: 50,
      });
    } else if (isSearchMode) {
      setIsSearchMode(false);
      refetch();
    }
  }, [debouncedSearchTerm, filterOrganism, search, refetch, isSearchMode]);
  
  // Fetch metadata info on mount
  useEffect(() => {
    const fetchMetadataInfo = async () => {
      try {
        const response = await repositoriesApi.getMetadataInfo();
        setMetadataInfo(response.data);
      } catch (error) {
        console.error('Failed to fetch metadata info:', error);
        setMetadataInfo({
          last_updated: '',
          total_datasets: 0,
          datasets_by_organism: {},
          metadata_loaded: false,
        });
      }
    };
    fetchMetadataInfo();
  }, []);
  
  // Handle organism change
  const handleOrganismChange = useCallback((newOrganism: string) => {
    setFilterOrganism(newOrganism);
    setSearchTerm('');
    setIsSearchMode(false);
  }, []);
  
  // Toggle dataset selection
  const toggleDataset = useCallback((projectId: string) => {
    setSelectedDatasets(prev => 
      prev.includes(projectId)
        ? []
        : [projectId]
    );
  }, []);
  
  // Toggle abstract expansion
  const toggleAbstract = useCallback((projectId: string) => {
    setExpandedAbstracts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  }, []);
  
  // Check if all selected datasets are downloaded or cached
  const allDownloadsComplete = useMemo(() => {
    if (selectedDatasets.length === 0) return false;
    if (!isInitiatingDownloads) return false;
    
    return selectedDatasets.every(projectId => {
      const jobId = projectJobMap.get(projectId);
      return jobId && isCompleted(jobId);
    });
  }, [selectedDatasets, projectJobMap, isCompleted, isInitiatingDownloads]);
  
  // Check if any downloads are in progress
  const hasDownloadsInProgress = useMemo(() => {
    if (!isInitiatingDownloads) return false;
    
    return selectedDatasets.some(projectId => {
      const jobId = projectJobMap.get(projectId);
      return jobId && isDownloading(jobId);
    });
  }, [selectedDatasets, projectJobMap, isDownloading, isInitiatingDownloads]);
  
  // Check if any downloads failed
  const hasFailedDownloads = useMemo(() => {
    return selectedDatasets.some(projectId => {
      const jobId = projectJobMap.get(projectId);
      return jobId && isFailed(jobId);
    });
  }, [selectedDatasets, projectJobMap, isFailed]);
  
  // Navigate when all downloads complete
  useEffect(() => {
    console.log('[PublicData] Navigation effect triggered:', {
      allDownloadsComplete,
      isInitiatingDownloads,
      isNavigating: isNavigatingRef.current,
      selectedDatasets,
    });
    
    if (allDownloadsComplete && isInitiatingDownloads && !isNavigatingRef.current) {
      console.log('[PublicData] Starting conversion and navigation flow...');
      isNavigatingRef.current = true; // Prevent duplicate execution
      
      const convertAndNavigate = async () => {
        try {
          console.log('[PublicData] Converting datasets:', selectedDatasets);
          setIsConverting(true); // Show conversion status
          
          // Convert each downloaded dataset to h5ad (if not already converted)
          for (const projectId of selectedDatasets) {
            try {
              console.log(`[PublicData] Checking overview for ${projectId}...`);
              const overview = await dataOverviewApi.getOverview(
                projectId,
                filterOrganism as 'human' | 'mouse'
              );
              console.log(`[PublicData] Overview result for ${projectId}:`, {
                has_h5ad: overview.data.has_h5ad,
                h5ad_path: overview.data.h5ad_path,
              });
              
              if (!overview.data.has_h5ad) {
                console.log(`[PublicData] Starting conversion for ${projectId}...`);
                const convertResult = await dataOverviewApi.convertToH5ad({
                  project_id: projectId,
                  organism: filterOrganism as 'human' | 'mouse',
                });
                console.log(`[PublicData] Conversion result for ${projectId}:`, convertResult.data);
                
                // Verify conversion succeeded by checking overview again
                console.log(`[PublicData] Verifying conversion for ${projectId}...`);
                const verifyOverview = await dataOverviewApi.getOverview(
                  projectId,
                  filterOrganism as 'human' | 'mouse'
                );
                console.log(`[PublicData] Verification result for ${projectId}:`, {
                  has_h5ad: verifyOverview.data.has_h5ad,
                });
                
                if (!verifyOverview.data.has_h5ad) {
                  console.error(`[PublicData] Conversion completed but h5ad not found for ${projectId}`);
                }
              } else {
                console.log(`[PublicData] H5ad already exists for ${projectId}, skipping conversion`);
              }
            } catch (error) {
              console.error(`[PublicData] Failed to convert ${projectId} to h5ad:`, error);
              // Continue with other datasets even if one fails
            }
          }
          
          setIsConverting(false); // Conversion complete
          
          // Navigate to dataset preparation page with selected datasets
          const navigationState = { 
            projectIds: selectedDatasets,
            organism: filterOrganism,
            isMerged: false,
          };
          console.log('[PublicData] Saving navigation state:', navigationState);
          saveNavigationState('dataset-preparation', navigationState);
          
          console.log('[PublicData] Navigating to /dataset-preparation...');
          navigate('/dataset-preparation', { 
            state: navigationState
          });
          console.log('[PublicData] Navigation called successfully');
          
          // Reset state after navigation completes
          setIsInitiatingDownloads(false);
          isNavigatingRef.current = false;
          console.log('[PublicData] Reset navigation state');
        } catch (error) {
          console.error('[PublicData] Error during h5ad conversion and navigation:', error);
          setIsConverting(false);
          setIsInitiatingDownloads(false);
          isNavigatingRef.current = false;
        }
      };
      
      convertAndNavigate();
    }
  }, [allDownloadsComplete, isInitiatingDownloads, selectedDatasets, navigate, filterOrganism]);
  
  // Handle continue
  const handleContinue = useCallback(async () => {
    if (selectedDatasets.length === 0 || isInitiatingDownloads) return;
    
    setIsInitiatingDownloads(true);
    
    try {
      const downloadPromises = selectedDatasets.map(async (projectId) => {
        const jobId = projectJobMap.get(projectId);
        if (jobId && isCompleted(jobId)) {
          return;
        }
        
        const dataset = datasets.find(d => d.project_id === projectId);
        if (dataset) {
          const newJobId = await startDownload({
            organism: dataset.organism,
            project_id: dataset.project_id,
            data_source: dataset.data_source,
          });
          
          if (newJobId) {
            setProjectJobMap(prevMap => new Map(prevMap).set(projectId, newJobId));
          }
        }
      });
      
      await Promise.all(downloadPromises);
    } catch (error) {
      console.error('Failed to initiate downloads:', error);
      setIsInitiatingDownloads(false);
    }
  }, [selectedDatasets, datasets, isInitiatingDownloads, projectJobMap, isCompleted, startDownload]);
  
  // Quota calculations
  const remainingAnalyses = (user?.publicAnalysesLimit || 0) - (user?.publicAnalysesUsed || 0);
  const isLimitReached = remainingAnalyses <= 0 && user?.tier === 'free';
  
  const loading = datasetsLoading || statsLoading;

  const steps = [
    { label: 'Upload', status: 'current' as const },
    { label: 'Validate', status: 'upcoming' as const },
    { label: 'Select Signature', status: 'upcoming' as const },
    { label: 'Run', status: 'upcoming' as const }
  ];

  return (
    <div className="min-h-screen bg-background pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Stepper steps={steps} />

        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate('/upload')}
            className="text-muted-foreground hover:text-foreground mb-4 flex items-center gap-2 transition-colors text-sm"
          >
            ← Back to upload options
          </button>
          <span className="font-mono text-[10px] tracking-[0.35em] text-muted-foreground/40 uppercase block mb-3">
            Dataset selection
          </span>
          <h1 className="text-foreground mb-2">Browse public datasets.</h1>
          <p className="text-muted-foreground">
            {stats?.total_datasets ? (
              `${stats.total_datasets.toLocaleString()} datasets available from recount3 (${organisms.join(', ')})`
            ) : (
              'Select publicly available datasets from recount3 and other repositories to analyze'
            )}
          </p>
        </div>
        
        {/* Metadata Warning Banner */}
        {metadataInfo && !metadataInfo.metadata_loaded && (
          <div className="p-4 mb-6 border border-border rounded-lg">
            <div className="flex items-start gap-3">
              <WarningCircle className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-foreground mb-1">Metadata not loaded</p>
                <p className="text-muted-foreground text-sm">
                  Study titles and abstracts may be unavailable. Contact an administrator to refresh the metadata.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Metadata Last Updated Info */}
        {metadataInfo && metadataInfo.metadata_loaded && metadataInfo.last_updated && (
          <div className="px-1 mb-6">
            <p className="text-sm text-muted-foreground">
              <span className="font-medium text-foreground">Metadata last updated:</span>{' '}
              {new Date(metadataInfo.last_updated).toLocaleDateString()}{' '}
              &mdash; {metadataInfo.total_datasets.toLocaleString()} datasets ({
                Object.entries(metadataInfo.datasets_by_organism)
                  .map(([org, count]) => `${count.toLocaleString()} ${org}`)
                  .join(', ')
              })
            </p>
          </div>
        )}
        
        {/* Enhanced Statistics */}
        {stats && stats.organisms && Object.keys(stats.organisms).length > 0 && (
          <div className="mb-6">
            <div className="grid gap-px bg-[#EAEAEA] border border-border rounded-xl overflow-hidden"
              style={{ gridTemplateColumns: `repeat(${Object.keys(stats.organisms).length}, 1fr)` }}
            >
              {Object.entries(stats.organisms).map(([organism, count]) => {
                const datasetCount = count as number;
                return (
                  <div key={organism} className="p-5 bg-background">
                    <p className="font-mono text-[10px] tracking-[0.3em] text-muted-foreground/40 uppercase mb-2 capitalize">{organism}</p>
                    <p className="text-2xl font-bold text-teal-600 font-display tabular-nums">
                      {datasetCount.toLocaleString()}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">datasets</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Usage Status */}
        <Card className="p-5 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {isLimitReached ? (
                <WarningCircle className="w-4 h-4 text-destructive flex-shrink-0" />
              ) : (
                <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" />
              )}
              <p className="text-sm text-muted-foreground">
                {isLimitReached ? (
                  <span className="text-destructive">Monthly limit reached for public analyses.</span>
                ) : user?.tier === 'free' ? (
                  <>{remainingAnalyses} of {user?.publicAnalysesLimit} free analyses remaining this month.</>
                ) : (
                  <>Unlimited public analyses available on your plan.</>
                )}
                {user?.tier === 'free' && !isLimitReached && (
                  <span className="ml-1 text-muted-foreground/70">Upgrade for unlimited access.</span>
                )}
              </p>
            </div>
            {user?.tier === 'free' && (
              <Button variant="default" size="sm" onClick={() => navigate('/pricing')}>
                Upgrade
              </Button>
            )}
          </div>
        </Card>

        {/* MagnifyingGlass and Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1 relative">
            <label htmlFor="dataset-search" className="sr-only">
              MagnifyingGlass datasets by project ID or study name
            </label>
            <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" aria-hidden="true" />
            <input
              id="dataset-search"
              type="text"
              placeholder="MagnifyingGlass by project ID (SRP..., ERP...) or study name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={loading}
              className="w-full pl-10 pr-10 py-2.5 border border-input rounded-lg bg-background text-foreground placeholder:text-muted-foreground focus:outline-hidden focus:ring-2 focus:ring-ring focus:border-transparent disabled:bg-muted disabled:cursor-not-allowed"
              aria-describedby="search-help"
            />
            <span id="search-help" className="sr-only">
              Type to search for datasets. Results will appear as you type.
            </span>
            {datasetsLoading && searchTerm && (
              <CircleNotch className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground animate-spin" aria-label="Searching" />
            )}
          </div>
          <div>
            <label htmlFor="organism-filter" className="sr-only">
              Filter by organism
            </label>
            <select
              id="organism-filter"
              value={filterOrganism}
              onChange={(e) => handleOrganismChange(e.target.value)}
              disabled={loading}
              className="px-4 py-2.5 border border-input rounded-lg bg-background text-foreground focus:outline-hidden focus:ring-2 focus:ring-ring focus:border-transparent disabled:bg-muted disabled:cursor-not-allowed"
              aria-label="Filter datasets by organism"
            >
              {organisms.map(organism => (
                <option key={organism} value={organism}>
                  {organism.charAt(0).toUpperCase() + organism.slice(1)}
                  {stats?.organisms?.[organism] ? ` (${stats.organisms[organism].toLocaleString()})` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Error State */}
        {datasetsError && (
          <Card className="p-6 mb-6 bg-destructive/10 border-destructive/20">
            <div className="flex items-start gap-3">
              <WarningCircle className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-destructive mb-1">
                  {datasetsError.status === 429 ? 'Rate Limit Exceeded' :
                   datasetsError.status === 502 || datasetsError.status === 503 ? 'Service Unavailable' :
                   datasetsError.status === 504 ? 'Request Timeout' :
                   !datasetsError.status ? 'Network Error' :
                   'Failed to load datasets'}
                </h3>
                <p className="text-destructive/80 text-sm mb-3">
                  {datasetsError.message}
                </p>
                {datasetsError.status === 429 && (
                  <p className="text-destructive/60 text-xs mb-3">
                    Please wait a few moments before trying again.
                  </p>
                )}
                <Button variant="secondary" onClick={refetch} size="sm">
                  Try Again
                </Button>
              </div>
            </div>
          </Card>
        )}

        {/* Loading Skeleton */}
        {datasetsLoading && datasets.length === 0 && (
          <div className="space-y-4 mb-6">
            {[1, 2, 3, 4, 5].map(i => (
              <Card key={i} className="p-6 animate-pulse">
                <div className="flex items-start gap-4">
                  <div className="w-6 h-6 bg-muted rounded"></div>
                  <div className="flex-1 space-y-3">
                    <div className="h-5 bg-muted rounded w-3/4"></div>
                    <div className="flex gap-2">
                      <div className="h-5 bg-muted rounded w-20"></div>
                      <div className="h-5 bg-muted rounded w-16"></div>
                    </div>
                    <div className="h-4 bg-muted rounded w-full"></div>
                    <div className="h-4 bg-muted rounded w-2/3"></div>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Dataset List */}
        {!datasetsLoading && datasets.length > 0 && (
          <div className="space-y-4 mb-6" role="list" aria-label="Available datasets">
            {datasets.map(dataset => {
              const isSelected = selectedDatasets.includes(dataset.project_id);
              const status = downloadStatusMap.get(dataset.project_id);
              const job = status?.job;
              const downloading = status?.downloading || false;
              const completed = status?.completed || false;
              const failed = status?.failed || false;
              const isReady = completed;
              
            return (
              <Card 
                key={dataset.project_id}
                className={`p-6 transition-all cursor-pointer ${
                  isSelected ? 'border-2 border-teal-600' : 'hover:border-teal-300'
                } ${isLimitReached ? 'opacity-50 cursor-not-allowed' : ''}`}
                onClick={() => !isLimitReached && toggleDataset(dataset.project_id)}
                role="listitem"
                tabIndex={isLimitReached ? -1 : 0}
                onKeyDown={(e) => {
                  if ((e.key === 'Enter' || e.key === ' ') && !isLimitReached) {
                    e.preventDefault();
                    toggleDataset(dataset.project_id);
                  }
                }}
                aria-label={`Dataset ${dataset.project_id}: ${dataset.study || dataset.project_id}`}
                aria-selected={isSelected}
                aria-disabled={isLimitReached}
              >
                <div className="flex items-start gap-4">
                  <div 
                    className={`w-6 h-6 rounded border-2 flex items-center justify-center flex-shrink-0 mt-1 pointer-events-none ${
                      isSelected ? 'bg-primary border-primary' : 'border-muted-foreground/30'
                    }`}
                  >
                    {isSelected && <CheckCircle className="w-5 h-5 text-primary-foreground" />}
                  </div>
                  
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex-1">
                        <h3 className="text-foreground mb-1">{dataset.study_title || dataset.study || dataset.project_id}</h3>
                        <p className="text-sm text-muted-foreground mb-2">{dataset.project_id}</p>
                        <div className="flex flex-wrap gap-2 mb-2">
                          <Badge variant="default">
                            <Database className="w-3 h-3 mr-1 inline" />
                            {dataset.data_source.toUpperCase()}
                          </Badge>
                          <Badge variant="outline">
                            {dataset.organism.charAt(0).toUpperCase() + dataset.organism.slice(1)}
                          </Badge>
                        </div>
                      </div>
                      
                      {/* DownloadSimple/Cache Status Indicator */}
                      <div className="ml-4">
                        {isSelected && downloading && (
                          <div className="flex items-center gap-2 text-primary">
                            <CircleNotch className="w-5 h-5 animate-spin" />
                            <span className="text-sm font-medium">
                              {job?.progress_percent ? `${job.progress_percent}%` : 'Downloading...'}
                            </span>
                          </div>
                        )}
                        {isReady && (
                          <div className="flex items-center gap-2 text-teal-600">
                            <CheckCircle className="w-5 h-5" />
                            <span className="text-sm font-medium">Ready</span>
                          </div>
                        )}
                        {isSelected && failed && !isReady && (
                          <div className="flex items-center gap-2">
                            <div className="flex items-center gap-2 text-destructive">
                              <XCircle className="w-5 h-5" />
                              <span className="text-sm font-medium">Failed</span>
                            </div>
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    const newJobId = await startDownload({
                                      organism: dataset.organism,
                                      project_id: dataset.project_id,
                                      data_source: dataset.data_source,
                                    });
                                    if (newJobId) {
                                      setProjectJobMap(prevMap => new Map(prevMap).set(dataset.project_id, newJobId));
                                    }
                                  } catch (error) {
                                    console.error('Retry download failed:', error);
                                  }
                                }}
                                aria-label="Retry failed download"
                              >
                                Retry
                              </Button>
                          </div>
                        )}
                      </div>
                    </div>              
                    
                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      {dataset.sample_count && (
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          <span>{dataset.sample_count.toLocaleString()} samples</span>
                        </div>
                      )}
                      {dataset.date_processed && (
                        <div className="flex items-center gap-2">
                          <CalendarBlank className="w-4 h-4" /> 
                          Added to database at:
                          <span>{new Date(dataset.date_processed).toLocaleDateString()}</span>
                        </div>
                      )}
                    </div>
                    
                    {/* Expandable Abstract */}
                    {dataset.study_abstract && (
                      <div className="mt-3">
                        <button
                          className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleAbstract(dataset.project_id);
                          }}
                          aria-expanded={expandedAbstracts.has(dataset.project_id)}
                          aria-label={expandedAbstracts.has(dataset.project_id) ? 'Collapse study abstract' : 'Expand study abstract'}
                        >
                          <CaretDown 
                            className={`w-4 h-4 transition-transform ${
                              expandedAbstracts.has(dataset.project_id) ? 'rotate-180' : ''
                            }`}
                          />
                          {expandedAbstracts.has(dataset.project_id) ? 'Hide' : 'Show'} Study Abstract
                        </button>
                        {expandedAbstracts.has(dataset.project_id) && (
                          <div className="mt-2 p-3 bg-muted rounded text-sm text-muted-foreground leading-relaxed">
                            {dataset.study_abstract}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
            
            {/* Load More Button */}
            {hasMore && !isSearchMode && (
              <div className="text-center pt-6">
                <Button
                  variant="secondary"
                  onClick={loadMore}
                  disabled={datasetsLoading}
                  aria-label="Load more datasets"
                  aria-busy={datasetsLoading}
                >
                  {datasetsLoading ? (
                    <><CircleNotch className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" /> Loading...</>
                  ) : (
                    'Load More Datasets'
                  )}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Empty State */}
        {!datasetsLoading && datasets.length === 0 && !datasetsError && (
          <div className="text-center py-12">
            <Database className="w-16 h-16 text-muted-foreground/50 mx-auto mb-4" />
            <h3 className="text-foreground mb-2">
              {searchTerm ? 'No datasets found' : 'No datasets available'}
            </h3>
            <p className="text-muted-foreground mb-4">
              {searchTerm ? (
                <>
                  No datasets match your search for "<span className="font-medium">{searchTerm}</span>".
                  <br />
                  Try searching by project ID (e.g., SRP009615) or study name.
                </>
              ) : (
                <>
                  No datasets are currently available for {filterOrganism}.
                  <br />
                  Try selecting a different organism or check back later.
                </>
              )}
            </p>
            {searchTerm && (
              <Button
                variant="secondary"
                onClick={() => setSearchTerm('')}
                className="mt-2"
              >
                Clear MagnifyingGlass
              </Button>
            )}
          </div>
        )}

        {/* Actions */}
        {selectedDatasets.length > 0 && (
          <div 
            className="sticky bottom-6 bg-card border border-border rounded-lg shadow-lg p-5 max-w-6xl mx-auto"
            role="region"
            aria-label="Selected datasets actions"
            aria-live="polite"
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-foreground mb-1">
                  1 dataset selected
                </p>
                {!isInitiatingDownloads && (
                  <p className="text-muted-foreground">
                    Click Continue to download and proceed to dataset preparation
                  </p>
                )}
                {isInitiatingDownloads && hasDownloadsInProgress && (
                  <p className="text-primary flex items-center gap-2">
                    <DownloadSimple className="w-4 h-4 animate-pulse" />
                    Downloading datasets...
                  </p>
                )}
                {hasFailedDownloads && !hasDownloadsInProgress && (
                  <p className="text-destructive flex items-center gap-2">
                    <WarningCircle className="w-4 h-4" />
                    Some downloads failed. Please retry.
                  </p>
                )}
                {allDownloadsComplete && isConverting && (
                  <p className="text-teal-600 flex items-center gap-2">
                    <CircleNotch className="w-4 h-4 animate-spin" />
                    Converting dataset and standardizing gene IDs...
                  </p>
                )}
                {allDownloadsComplete && !isConverting && (
                  <p className="text-teal-600 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Dataset ready. Redirecting...
                  </p>
                )}
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="secondary"
                  onClick={() => {
                    setSelectedDatasets([]);
                    setProjectJobMap(new Map());
                    setIsInitiatingDownloads(false);
                  }}
                  disabled={hasDownloadsInProgress}
                  aria-label="Clear all selected datasets"
                >
                  Clear Selection
                </Button>
                <Button 
                  variant="default"
                  onClick={handleContinue}
                  disabled={allDownloadsComplete || isInitiatingDownloads}
                  aria-label={
                    isConverting ? 'Converting dataset, please wait' :
                    allDownloadsComplete ? 'Redirecting to dataset preparation' :
                    isInitiatingDownloads ? 'Downloading dataset, please wait' :
                    'Continue to dataset preparation'
                  }
                  aria-busy={isInitiatingDownloads || allDownloadsComplete}
                >
                  {isInitiatingDownloads && !allDownloadsComplete ? (
                    <>
                      <CircleNotch className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                      Downloading dataset...
                    </>
                  ) : isConverting ? (
                    <>
                      <CircleNotch className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                      Converting dataset...
                    </>
                  ) : allDownloadsComplete ? (
                    <>
                      <CircleNotch className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
                      Redirecting...
                    </>
                  ) : (
                    'Continue to Dataset Preparation'
                  )}
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
